import { SweetAlertService } from 'src/app/services/sweetalert.service';
import { Component, Input, OnInit, ViewChild, ElementRef, HostListener } from '@angular/core';
import { FileUploadService } from 'src/app/services/shared/file-upload.service';
import { Observable } from 'rxjs';
import { map, takeWhile, concatMap } from 'rxjs/operators';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Distributor } from 'src/app/models/distributor-model';
import { DistributorService } from 'src/app/services/distributor/distributor.service';
import { MasterDataService } from 'src/app/services/master-data.service';
import { SpinnerService } from 'src/app/services/shared/spinner.service';
import { forkJoin } from 'rxjs';
import { SubscriptionsService } from 'src/app/services/shared/subscriptions/subscriptions.service';
import { Continent, State, Country, City } from 'src/app/models/master-data.model';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import Swal from 'sweetalert2';


@Component({
  selector: 'app-oem-company-information',
  templateUrl: './oem-company-information.component.html',
  styleUrls: ['./oem-company-information.component.css']
})
export class OemCompanyInformationComponent implements OnInit {

  @Input() companyInfo!: FormGroup;
  @Input() isPopUpVisible: boolean = false;
  @Input() isCertificatePopUpVisible: boolean = false;
  informationFields: { name: string, label: string }[] = [];
  certificateFields: { name: string, label: string }[] = [];
  continents!: any[];
  countries!: any[];
  subscriptionPlan: string = '';
  status: string = '';
  subscriptionName: string = '';
  subscriptionImage: string = '';
  states!: any[];
  tooltipVisible: string | null = null;
  cities!: any[];
  isOnline: boolean = navigator.onLine;
  @ViewChild('logoInput') logoInput!: ElementRef;
  @ViewChild('otherFilesInput') otherFilesInput!: ElementRef;
  @ViewChild('scrollContainer') scrollContainer: ElementRef = new ElementRef(null);

  filteredCities: City[] = [];
  filteredCountries: any[] = [];
  filteredStates: any[] = [];
  filteredContinents: any[] = [];
  cityControl = new FormControl();
  countryControl = new FormControl();
  stateControl = new FormControl();
  continentControl = new FormControl();

  triggerLogoInputClick() {
    this.logoInput.nativeElement.click();
  }

  get showInformationFields(): boolean {
    return this.isPopUpVisible && !this.isCertificatePopUpVisible;
  }

  get showCertificateFields(): boolean {
    return this.isPopUpVisible && this.isCertificatePopUpVisible;
  }

  get showBothFields(): boolean {
    return !this.isPopUpVisible;
  }


  triggerOtherFilesInputClick() {
    this.otherFilesInput.nativeElement.click();
  }

  constructor(private fileUploadService: FileUploadService, private masterDataService: MasterDataService, private fb: FormBuilder, private spinnerService: SpinnerService, private sweetAlertService: SweetAlertService
    , private subscriptionService: SubscriptionsService
  ) { }

  async ngOnInit(): Promise<void> {
    this.spinnerService.showSpinner();
    this.isOnline = navigator.onLine;
    const companyInformationGroup = this.companyInfo.get('companyInformation') as FormGroup;
    if (companyInformationGroup) {
      const fields = Object.keys(companyInformationGroup.controls)
        .filter(key => !['firstName', 'lastName', 'createdBy'].includes(key))
        .map(key => ({
          name: key,
          label: this.getFieldLabel(key),
        }));

      if (!this.isPopUpVisible) {
        this.informationFields = fields
          .filter(field => !this.isCertificateField(field.name) && !this.ignoreFields(field.name))
          .sort(this.sortFields([
            'userName', 'email', 'phoneNumber', 'uniqueId', 'logo', 'name', 'brandName', 'year', 'url',
            'employeeCount', 'continent', 'country', 'state', 'city', 'address', 'zipCode'
          ]));
      } else {
        this.informationFields = fields
          .filter(field => !this.isCertificateField(field.name) && !this.ignoreFields(field.name))
          .sort(this.sortFields([
            'userName', 'employeeCount', 'email', 'continent', 'phoneNumber', 'country', 'uniqueId', 'state',
            'logo', 'city', 'name', 'address', 'brandName', 'zipCode', 'year', 'url'
          ]));
      }
      this.certificateFields = fields
        .filter(field => this.isCertificateField(field.name))
        .sort(this.sortFields([
          'crcopy', 'cecopy', 'businessLicense', 'europeanCertificate', 'otherFiles'
        ]));

      await this.loadSubscriptions(companyInformationGroup).then(() => {
      });
      await this.isStatus()
      if (!this.showCertificateFields) {
        await this.loadMasterData().then(() => {
          this.setFormValues().then(() => {
          });
        });
      }

      this.continentControl.setValue(this.companyInfo.get('companyInformation')?.get('continent')?.value);
      this.countryControl.setValue(this.companyInfo.get('companyInformation')?.get('country')?.value);
      this.stateControl.setValue(this.companyInfo.get('companyInformation')?.get('state')?.value);
      this.cityControl.setValue(this.companyInfo.get('companyInformation')?.get('city')?.value);
    }
    this.spinnerService.hideSpinner();
  }

  isStatus(): Promise<void> {
    return new Promise<void>((resolve) => {
      setTimeout(() => {
        this.status = this.companyInfo.get('companyInformation')?.get('status')?.value;
        resolve();
      }, 1000);
    });
  }

  getFieldLabel(key: string): string {
    const labels: { [key: string]: string } = {
      email: 'Email',
      userName: 'User Name',
      uniqueId: 'Unique ID',
      year: 'Established Year',
      name: 'Company Name',
      phoneNumber: 'Phone Number',
      employeeCount: 'No.of Employees',
      continent: 'Continent',
      country: 'Country',
      state: 'State',
      city: 'City',
      address: 'Address',
      zipCode: 'Zip Code',
      logo: 'Company Logo',
      url: 'Website URL',
      europeanCertificate: 'CE Certificate',
      brandName: 'Brand Name',
      crcopy: 'CR Copy',
      businessLicense: 'Business License',
      cecopy: 'CE Copy*',
      otherFiles: 'Other Files',
      subscriptionInfoId: 'Subscription',
      branches: 'Branches'
    };
    return labels[key] || key;
  }

  private sortFields(order: string[]) {
    return (a: { name: string }, b: { name: string }) => order.indexOf(a.name) - order.indexOf(b.name);
  }

  private async loadSubscriptions(companyInformationGroup: FormGroup): Promise<void> {
    const value = companyInformationGroup.get('subscriptionInfoId')?.value;

    if (value) {
      await this.subscriptionService.getAllSubscriptions().toPromise().then(
        (response: any) => {
          if (response && response.data) {
            const subscription = response.data.find((sub: any) => sub.id === value);
            if (subscription) {
              this.subscriptionName = subscription.name;
              this.subscriptionImage = subscription.image;
            } else {
              this.subscriptionName = 'Free Plan';
            }
          }
        }
      ).catch(error => {
      });
    } else {
      await this.subscriptionService.getAllSubscriptions().toPromise().then(
        (response: any) => {
          if (response && response.data && response.data.length > 0) {
            const firstSubscription = response.data[0];
            companyInformationGroup.get('subscriptionInfoId')?.setValue(firstSubscription.id);
            this.subscriptionName = firstSubscription.name;
            this.subscriptionImage = firstSubscription.image;
          } else {
            this.subscriptionName = 'Free Plan';
          }
        }
      ).catch(error => {
      });
    }
  }

  private async loadMasterData() {
    try {
      const results = await forkJoin({
        continents: this.masterDataService.getContinents(),
      }).toPromise();

      this.continents = results?.continents.data;
    } catch (error) {
    }

    await this.loadContinents();
    await this.loadCountries(['']);
    await this.loadStates(['']);
    await this.loadCities(['']);
  }

  private async setFormValues() {
    const lead = this.companyInfo.get('companyInformation')?.value;
    if (lead) {
      await this.loadCountries([lead.continent]);
      await this.loadStates([lead.country]).then(async () => {
        await this.loadCities([lead.state]);
      });
    }
  }

  onInputChangeNumbers(event: any, fieldName: string): void {
    const inputValue = event.target.value;
    let sanitizedValue = inputValue.replace(/[^0-9]/g, '');
    if (fieldName === 'phoneNumber') {
      sanitizedValue = sanitizedValue.replace(/^(\d{3})(\d{3})(\d{4})$/, '($1) $2-$3');
    } else if (fieldName === 'establishedYear' || fieldName === 'zipCode') {
      sanitizedValue = sanitizedValue.replace(/\D/g, '').slice(0, 4);
    }
    this.companyInfo.get(fieldName)?.setValue(sanitizedValue);
  }

  isFieldInvalid(fieldName: string): boolean {
    if (['crcopy', 'cecopy', 'businessLicense'].includes(fieldName)) {
      const field = this.companyInfo.get('companyInformation.' + fieldName);
      return !!field?.touched && !field?.value;
    }
    return false;
  }

  isCertificateField(key: string): boolean {
    const certificateFields = ['otherFiles', 'crcopy', 'businessLicense', , 'europeanCertificate'];
    return certificateFields.includes(key);
  }

  ignoreFields(key: string): boolean {
    const ignoreFields = ['status', 'cecopy', 'description', 'branches', 'branchesPresent', 'subscriptionInfoId', 'branches', 'distributors', 'oemId', 'phoneCode'];
    return ignoreFields.includes(key);
  }

  numberOnly(event: any): boolean {
    const charCode = event.which ? event.which : event.keyCode;
    return !(charCode > 31 && (charCode < 48 || charCode > 57));
  }

  private uploadFile(file: File): Observable<string> {
    const formData = new FormData();
    formData.append('file', file);
    let randomNumber = Math.floor(Math.random() * 10) + 1;

    formData.append('destinationKey', randomNumber + '');

    return this.fileUploadService.uploadFile(formData).pipe(
      map((response: any) => {
        if (response && response.data) {
          return response.data;
        } else {
          throw new Error('Invalid response format from server.');
        }
      })
    );
  }

  fileSizeError: { [key: string]: boolean } = {};
  @HostListener('window:online')
  checkConnectionOnline() {
    this.isOnline = true;
  }

  @HostListener('window:offline')
  checkConnectionOffline() {
    this.isOnline = false;
  }
  selectFile(event: Event, controlName: string): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length) {
      const file = input.files[0];

      if (!this.isOnline) {
        Swal.fire({
          icon: 'error',
          title: 'No Internet Connection',
          text: 'Check Your Internet Connection and please try again.',
          confirmButtonText: 'Ok',
          confirmButtonColor: '#3085d6',
        });
        this.resetFileInput(controlName);
        return;
      }
      this.fileSizeError[controlName] = false;
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        Swal.fire(
          'Error',
          'Only JPEG, JPG, and PNG files are allowed',
          'error'
        );
        this.resetFileInput(controlName);
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        Swal.fire('Error', 'File size should not exceed 10MB', 'error');
        this.resetFileInput(controlName);
        return;
      }

      this.uploadFile(file).subscribe(
        (url: string) => {
          const companyInformation = this.companyInfo.get('companyInformation') as FormGroup;
          companyInformation.patchValue({
            [controlName]: url
          });
          this.resetFileInput(controlName);
        },
        (error) => {
          this.spinnerService.hideSpinner();

        }
      );
    }
  }


  private resetFileInput(controlName: string): void {
    const inputElement = document.getElementById(controlName) as HTMLInputElement;
    if (inputElement) {
      inputElement.value = '';
    }
  }


  selectFiles(event: Event, fieldName: string): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const filesArray = Array.from(input.files);
      this.fileSizeError[fieldName] = false;
      const oversizedFile = filesArray.find(file => file.size > 10 * 1024 * 1024);
      if (oversizedFile) {

        Swal.fire('Error', 'One or more files exceed the maximum size of 10MB.', 'error');
        this.resetFileInput(fieldName);
        return;
      }

      this.uploadFiles(input.files, fieldName);
      input.value = '';
    }
  }
  uploadFiles(files: FileList, fieldName: string): void {
    this.spinnerService.showSpinner();
    const fileArray: File[] = Array.from(files);

    const existingFiles = this.companyInfo.get('companyInformation.' + fieldName)?.value || [];

    let randomNumber = Math.floor(Math.random() * 10) + 1;
    this.fileUploadService.uploadFilesAndGetUrls(fileArray, randomNumber + '').subscribe(
      (urls: string[]) => {

        this.companyInfo.get('companyInformation.' + fieldName)?.setValue([...existingFiles, ...urls]);
        this.spinnerService.hideSpinner();
      },
      (error) => {
        this.sweetAlertService.showError('Failed to add file', ''),
          this.spinnerService.hideSpinner();
      }
    );
  }

  clearFile(fieldName: string, index?: number): void {
    if (fieldName === 'otherFiles' && typeof index === 'number') {
      const files = this.companyInfo.get('companyInformation.otherFiles')?.value as string[];
      files.splice(index, 1);
      this.companyInfo.get('companyInformation.otherFiles')?.setValue(files);
    } else {
      this.companyInfo.get('companyInformation.' + fieldName)?.reset();
    }
  }

  getFileName(url: string): string {
    const parts = url.split('/');
    const fileName = parts[parts.length - 1];
    if (fileName[0].match(/\d/)) {
      return fileName.substring(1);
    }
    return fileName;
  }

  showTooltip(fieldName: string) {
    this.tooltipVisible = fieldName;
  }

  hideTooltip(fieldName: string) {
    this.tooltipVisible = null;
  }

  getFileWidth(url: string): number {
    const fileName = this.getFileName(url);
    const charWidth = 10;
    const width = fileName.length * charWidth;
    return width;
  }

  onContinentChange(event: any) {
    const continentName = event?.value;
    this.loadCountries([continentName]);
  }

  onCountryChange(event: any) {
    const countryName = event?.value;
    if (countryName) {
      const selectedCountry = this.countries.find(country => country.countryName === countryName);
      if (selectedCountry) {
        const phoneCode = selectedCountry.phoneCode;
        this.companyInfo.get('companyInformation.phoneCode')?.setValue(phoneCode);
        this.loadStates([countryName]);
      }
    }
  }

  onStateChange(event: any) {
    const stateName = event?.value;
    this.loadCities([stateName]);
  }

  onCityChange(event: any) { }

  loadContinents(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.masterDataService.getContinents().subscribe(data => {
        this.filteredContinents = data.data;


        resolve();
      }, error => {
        reject(error);
      });
    });
  }

  loadCountries(continentNames: string[]): Promise<void> {
    return new Promise((resolve, reject) => {
      let pageNumber = 0;
      const pageSize = 100;
      let allCountries: any[] = [];
      let morePages = true;

      this.masterDataService.getCountries(pageNumber, pageSize, '', continentNames)
        .pipe(
          takeWhile(() => morePages),
          concatMap(response => {
            if (response.data.length === 0) {
              morePages = false; 
              return [];
            }
            allCountries = allCountries.concat(response.data);
            pageNumber++;
            return this.masterDataService.getCountries(pageNumber, pageSize, '', continentNames);
          })
        )
        .subscribe(
          data => {
            if (data.data.length === 0) {
              morePages = false;
            } else {
              allCountries = allCountries.concat(data.data);
            }
            this.countries = allCountries;
            this.filteredCountries = allCountries;
            resolve();
          },
          error => {
            reject(error);
          },
          () => {
            resolve();
          }
        );
    });
  }

  loadStates(countryNames: string[]): Promise<void> {
    return new Promise((resolve, reject) => {
      let pageNumber = 0;
      const pageSize = 500;
      let allStates: any[] = [];
      let morePages = true;

      this.masterDataService.getStates(pageNumber, pageSize, '', countryNames)
        .pipe(
          takeWhile(() => morePages),
          concatMap(response => {
            if (response.data.length === 0) {
              morePages = false; 
              return [];
            }
            allStates = allStates.concat(response.data);
            pageNumber++;
            return this.masterDataService.getStates(pageNumber, pageSize, '', countryNames);
          })
        )
        .subscribe(
          data => {
            if (data.data.length === 0) {
              morePages = false;
            } else {
              allStates = allStates.concat(data.data);
            }
            this.states = allStates;
            this.filteredStates = allStates;
            resolve();
          },
          error => {
            reject(error);
          },
          () => {
            resolve();
          }
        );
    });
  }

  loadCities(stateNames: string[]): Promise<void> {
    return new Promise((resolve, reject) => {
      let pageNumber = 0;
      const pageSize = 500;
      let allCities: any[] = [];
      let morePages = true;

      this.masterDataService.getCities(pageNumber, pageSize, '', stateNames)
        .pipe(
          takeWhile(() => morePages),
          concatMap(response => {
            if (response.data.length === 0) {
              morePages = false;
              return [];
            }
            allCities = allCities.concat(response.data);
            pageNumber++;
            return this.masterDataService.getCities(pageNumber, pageSize, '', stateNames);
          })
        )
        .subscribe(
          data => {
            if (data.data.length === 0) {
              morePages = false;
            } else {
              allCities = allCities.concat(data.data);
            }
            this.cities = allCities;
            this.filteredCities = allCities;
            resolve();
          },
          error => {
            reject(error);
          },
          () => {
            resolve();
          }
        );
    });
  }

  hasUploadedFiles(fieldName: string): boolean {
    const files = this.companyInfo.get(`companyInformation.${fieldName}`)?.value;
    return files && files.length > 0;
  }

  onOptionSelected($event: any, type: string) {
    switch (type) {
      case 'continent':
        this.companyInfo.get('companyInformation.continent')?.setValue($event.option.value);
        this.companyInfo.get('companyInformation.country')?.reset();
        this.companyInfo.get('companyInformation.state')?.reset();
        this.companyInfo.get('companyInformation.city')?.reset();
        this.onContinentChange({ value: $event.option.value });

        break;
      case 'country':

        this.companyInfo.get('companyInformation.country')?.setValue($event.option.value);

        this.companyInfo.get('companyInformation.state')?.reset();
        this.companyInfo.get('companyInformation.city')?.reset();
        this.onCountryChange({ value: $event.option.value });
        break;

      case 'state':

        this.companyInfo.get('companyInformation.state')?.setValue($event.option.value);

        this.companyInfo.get('companyInformation.city')?.reset();
        this.onStateChange({ value: $event.option.value });
        break;

      case 'city':

        this.companyInfo.get('companyInformation.city')?.setValue($event.option.value);
        this.onCityChange({ value: $event.option.value });
        break;

      default:
    }
  }
  onInputChangeContinent() {
    const inputValueContinent = this.continentControl.value?.toLowerCase() || '';
    this.filteredContinents = this.continents.filter(continent => continent.continentName.toLowerCase().includes(inputValueContinent));
  }
  onInputChangeCountry() {
    const inputValueCountry = this.countryControl.value?.toLowerCase() || '';
    this.filteredCountries = this.countries.filter(country => country.countryName.toLowerCase().includes(inputValueCountry));
  }
  onInputChangeState() {
    const inputValueState = this.stateControl.value?.toLowerCase() || '';
    this.filteredStates = this.states.filter(state => state.stateName.toLowerCase().includes(inputValueState));
  }
  onInputChangeCity() {
    const inputValue = this.cityControl.value?.toLowerCase() || '';
    this.filteredCities = this.cities.filter(city => city.cityName.toLowerCase().includes(inputValue));
  }

  validateInputContinent() {
    const inputValueContinent = this.continentControl.value;
    const validContinent = this.filteredContinents.some(continent => continent.continentName === inputValueContinent);

    if (!validContinent) {
      this.continentControl.setValue(''); 
    }
  }
  validateInputCountry() {
    const inputValueCountry = this.countryControl.value;
    const validCountry = this.filteredCountries.some(country => country.countryName === inputValueCountry);

    if (!validCountry) {
      this.countryControl.setValue(''); 
    }
  }
  validateInputState() {
    const inputValueState = this.stateControl.value;
    const validState = this.filteredStates.some(state => state.stateName === inputValueState);

    if (!validState) {
      this.stateControl.setValue(''); 
    }
  }
  validateInputCity() {
    const inputValue = this.cityControl.value;
    const validCity = this.filteredCities.some(city => city.cityName === inputValue);

    if (!validCity) {
      this.cityControl.setValue('');
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  onDrop(event: any, fieldName: string) {
    event.preventDefault();
    if (fieldName !== 'otherFiles') {
      const input = event?.dataTransfer
      if (input.files && input.files.length) {
        const file = input.files[0];
        this.uploadFile(file).subscribe(
          (url: string) => {
            const companyInformation = this.companyInfo.get('companyInformation') as FormGroup;
            companyInformation.patchValue({
              [fieldName]: url
            });
            this.resetFileInput(fieldName);
          },
          (error) => {
            this.spinnerService.hideSpinner()
            this.sweetAlertService.showError('Failed to add file', '');
          }
        );
      }
    } else {
      const files = event?.dataTransfer?.files
      if (files) {
        this.uploadFiles(files, fieldName);
      }
    }
  }

openFile(file: any): void {
  const fileUrl = file; 
  window.open(fileUrl, '_blank');
}

getFileURL(file: any): string {
  if (typeof file === 'string') {
    return file; 
  } else if (file instanceof File || file instanceof Blob) {
    return URL.createObjectURL(file);
  }
  return '';
}

}
